/**
 * Unified KPI catalog - the SYSTEM of record (D-019 revised, Framework-first).
 *
 * Sources: management's "KPI Framework - Trucking Operations" doc (the master
 * list, by department) merged with the "KPI Scorecard Template" (an executive
 * VIEW of the same numbers, by division). Every Framework KPI is here,
 * including the ones the Scorecard dropped; Scorecard-only additions are
 * merged in; duplicates are held once (dedupe notes below). 47 KPIs total.
 *
 * Content-in-code per D-013: definitions/targets/owners change only when
 * management changes them. Entered/computed VALUES live in `kpi_entries`
 * (migration 0020) keyed by the stable `id` here - NEVER rename an id.
 *
 * Data sources & automation statuses are grounded in the 2026-08-14 live API
 * probes (see docs/FEATURE-REQUESTS.md "Live API probe findings"):
 *  - QuickManage trips: hauling_rate + per-stop distance/deadhead confirmed.
 *  - Fleetio: vehicle_type splits divisions; costs live in work orders.
 *  - Samsara global token: HOS daily-log duty durations confirmed.
 *  - FMCSA public data verified for USDOT 2404871 / MC-827328.
 *
 * Dedupe decisions (agreed 2026-08-14):
 *  - Framework "invoice error rate" == Scorecard "Invoice Accuracy %" inverted
 *    -> kept once as invoice-accuracy.
 *  - Framework "customer retention" == Scorecard "Contract Retention %"
 *    -> kept once as contract-retention.
 *  - Billing cycle keeps the Framework's direct/factoring split; the
 *    Scorecard's single "Billing Cycle Time" (<= 3 days) maps to the direct row.
 *  - Flagged, NOT merged: driver-utilization formula conflict (Q10) and
 *    avg-rate-per-mile vs loaded-rate-per-mile near-duplicate (Sales to confirm).
 *
 * Parked (Framework "Optional/TBD", not in the catalog until confirmed):
 * toll cost per mile (tanker + van), layover-pay driver-utilization variant.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Department =
  | "company"
  | "accounting"
  | "tank-dispatch"
  | "dryvan-dispatch"
  | "sales"
  | "safety"
  | "fleet";

export type Division = "company" | "tanker" | "dryvan";

/** Scorecard view sections (the Scorecard doc's own grouping). */
export type ScorecardSection =
  | "company"
  | "tanker-ops"
  | "tanker-safety"
  | "fleet-liquid"
  | "dryvan-ops"
  | "fleet-dry"
  | "sales"
  | "accounting";

export type KpiUnit =
  | "percent"
  | "days"
  | "usd"
  | "count"
  | "usdPerMile"
  | "hours"
  | "perMillionMiles";

export type KpiDirection =
  /** Green when value <= threshold (e.g. OR <= 92%). */
  | "at_most"
  /** Green when value >= threshold (e.g. OTD >= 98%). */
  | "at_least"
  /** No numeric threshold - improvement means the value goes DOWN. */
  | "lower_better"
  /** No numeric threshold - improvement means the value goes UP. */
  | "higher_better"
  /** Informational only (target set annually/quarterly or "controlled"). */
  | "info";

export type KpiSourceSystem =
  | "quickmanage"
  | "samsara"
  | "fleetio"
  | "fmcsa"
  | "recorder"
  | "accounting"
  | "manual";

/** How the value arrives once Phase D collectors ship. */
export type KpiAutomation = "auto" | "semi" | "manual";

/**
 * QuickManage APIs that would unlock or tighten a KPI. Vendor status 2026-08-26.
 * Get Trailer is documented but not shown on tanker/van rows — those already fill
 * from trailer make. Reports UI notes: billing-cycle + AR. Accounting rows
 * that QM cannot fill (OR, cash-to-invoice, invoice accuracy, close days) are
 * tagged `qm-cannot`. Full 47-KPI map is src/features/kpi/qm-reports.ts
 * and docs/KPI-QM-REPORTS.md — do not re-probe a bare GET /x/reports.
 */
export type QmWaitingId =
  | "payroll"
  | "payroll-partial"
  | "get-trailer"
  | "reports"
  /** QM is related, but Search/Reports/payroll cannot fill this row. */
  | "qm-cannot";

/**
 * KPIs with no outside system: staff log events in the portal (Phase F).
 * Count/sum KPIs can roll the log into the period value; rates stay typed in.
 */
export const LOG_KPI_IDS = new Set([
  "customer-service-incidents",
  "new-direct-customers",
  "preventable-accidents",
  "training-completion",
  "pipeline-value",
]);

export interface KpiDef {
  /** Stable slug - also the `kpi_id` stored with entries. NEVER rename. */
  id: string;
  department: Department;
  division: Division;
  name: string;
  /** Formula/definition as management's docs word it. */
  formula: string;
  owner: string;
  unit: KpiUnit;
  direction: KpiDirection;
  /** Numeric bound for at_most / at_least directions. */
  threshold?: number;
  /** Target exactly as written (or "TBD (Q3)" for Framework-only KPIs). */
  targetLabel: string;
  /** Where the Scorecard view shows it; null = Framework-only KPI. */
  scorecardSection: ScorecardSection | null;
  /** Systems the value comes from once automated. */
  sources: KpiSourceSystem[];
  /** One-line, probe-grounded note on how the number is computed. */
  sourceNote: string;
  automation: KpiAutomation;
  /** Open flags: management questions (Q3..Q11) or dedupe/conflict notes. */
  flags?: string[];
  /** What staff must keep doing in the source system, or this Auto number goes stale/zero. Shown in red. */
  staffKeepAlive?: string;
  /**
   * Set when a collector COULD produce a number but that number would not
   * measure what the KPI claims. Rendered instead of a value, so a broken
   * measurement is never mistaken for performance.
   */
  notMeasurable?: string;
  /**
   * QuickManage APIs that would unlock or tighten this row. Shown as an amber
   * badge so we know which KPIs to revisit when the vendor ships them.
   */
  qmWaiting?: QmWaitingId[];
}

export interface DepartmentDef {
  id: Department;
  title: string;
  blurb: string;
}

export interface ScorecardSectionDef {
  id: ScorecardSection;
  title: string;
  blurb: string;
}

// ---------------------------------------------------------------------------
// Groupings
// ---------------------------------------------------------------------------

/** Framework grouping - the system of record's own structure. */
export const DEPARTMENTS: DepartmentDef[] = [
  {
    id: "company",
    title: "Company-Wide",
    blurb: "Shared accountability across Dispatch, Fleet, Accounting, and Safety.",
  },
  {
    id: "accounting",
    title: "Accounting",
    blurb: "Billing, AR, settlements, and reporting discipline.",
  },
  {
    id: "tank-dispatch",
    title: "Liquid Tank Dispatch",
    blurb: "Higher risk, higher compliance, contract-driven liquid bulk work.",
  },
  {
    id: "dryvan-dispatch",
    title: "Dry Van Dispatch",
    blurb: "Utilization, margin discipline, and driver productivity.",
  },
  {
    id: "sales",
    title: "Sales (Liquid Tank)",
    blurb: "Direct-customer growth and retention.",
  },
  {
    id: "safety",
    title: "Safety",
    blurb: "DOT / FMCSA / HazMat discipline across the fleet.",
  },
  {
    id: "fleet",
    title: "Fleet / Maintenance",
    blurb: "Equipment health, availability, and maintenance economics.",
  },
];

/** Scorecard grouping - the executive view's structure (a filter, not data). */
export const SCORECARD_SECTIONS: ScorecardSectionDef[] = [
  {
    id: "company",
    title: "Company-Wide",
    blurb: "Shared accountability across both divisions.",
  },
  {
    id: "tanker-ops",
    title: "Liquid Tanker - Operational",
    blurb: "Contract-driven liquid bulk performance.",
  },
  {
    id: "tanker-safety",
    title: "Liquid Tanker - Safety & Compliance",
    blurb: "DOT / FMCSA / HazMat discipline.",
  },
  {
    id: "fleet-liquid",
    title: "Fleet - Liquid",
    blurb: "Tanker equipment health.",
  },
  {
    id: "dryvan-ops",
    title: "Dry Van - Operational",
    blurb: "Utilization and driver productivity.",
  },
  {
    id: "fleet-dry",
    title: "Fleet - Dry Van",
    blurb: "Van/reefer equipment health.",
  },
  {
    id: "sales",
    title: "Sales (Liquid Tank - Direct Customers)",
    blurb: "Direct-customer growth and retention.",
  },
  {
    id: "accounting",
    title: "Accounting",
    blurb: "Billing, settlements, and reporting discipline.",
  },
];

// ---------------------------------------------------------------------------
// The catalog - 47 KPIs
// ---------------------------------------------------------------------------

export const KPI_DEFS: KpiDef[] = [
  // ==== Company-Wide (6) ====================================================
  {
    id: "operating-ratio",
    department: "company",
    division: "company",
    name: "Operating Ratio (OR)",
    formula: "Operating expense ÷ operating revenue",
    owner: "Ownership",
    unit: "percent",
    direction: "at_most",
    threshold: 92,
    targetLabel: "≤ 92%",
    scorecardSection: "company",
    sources: ["accounting"],
    sourceNote:
      "P&L from QuickBooks — type it in. No QuickBooks access (Q6). QuickManage has the revenue side (trips Search) but no operating expense. Statement Reports are driver pay, not the company P&L.",
    automation: "manual",
    flags: ["Q6"],
  },
  {
    id: "revenue-per-truck-week",
    department: "company",
    division: "company",
    name: "Revenue per Truck / Week",
    formula: "Completed-trip revenue ÷ trucks that hauled ÷ weeks",
    owner: "Dispatch",
    unit: "usd",
    direction: "info",
    targetLabel: "Target by division",
    scorecardSection: "company",
    sources: ["quickmanage", "samsara"],
    sourceNote:
      "Completed QuickManage loads in the month (status delivered, invoiced, or closed): hauling rate + accessorials, divided by the unique truck units on those loads, then divided by weeks in the month (calendar days ÷ 7). Only trucks that hauled at least one of those loads are in the divisor — idle trucks, and trucks that are inactive or sold, are not counted. This is average weekly revenue per working truck, not per truck on the books. Company-driver and owner-operator loads are both in the total; expand the row to see the split.",
    automation: "auto",
  },
  {
    id: "cash-to-invoice-days",
    department: "company",
    division: "company",
    name: "Cash-to-Invoice Days",
    formula: "Average days from delivery to cash",
    owner: "Accounting",
    unit: "days",
    direction: "at_most",
    threshold: 35,
    targetLabel: "≤ 35 days",
    scorecardSection: "company",
    sources: ["accounting"],
    sourceNote:
      "Needs a payment/cash date from QuickBooks. No QuickBooks access (Q6). Receivable Report has Customer Paid Amt (dollars) and Invoiced Date, not days-to-cash. Trip Report has Payment Status, not a paid-on date.",
    automation: "manual",
    flags: ["Q6"],
  },
  {
    id: "on-time-pickup",
    department: "company",
    division: "company",
    name: "On-Time Pickup %",
    formula: "On-time pickups ÷ total pickups",
    owner: "Dispatch",
    unit: "percent",
    direction: "higher_better",
    targetLabel: "TBD (Q3)",
    scorecardSection: null,
    sources: ["quickmanage", "recorder"],
    sourceNote:
      "QM appointment vs a REAL recorder status change (from_status set). First snapshots are not arrivals (Q5).",
    automation: "semi",
    flags: ["Q3", "Q5"],
  },
  {
    id: "on-time-delivery",
    department: "company",
    division: "company",
    name: "On-Time Delivery %",
    formula: "On-time deliveries ÷ total deliveries",
    owner: "Dispatch",
    unit: "percent",
    direction: "at_least",
    threshold: 98,
    targetLabel: "≥ 98%",
    scorecardSection: "company",
    sources: ["quickmanage", "recorder"],
    sourceNote: "Same as pickup: only real delivered transitions vs appointment (Q5). First snapshots ignored.",
    automation: "semi",
    flags: ["Q5"],
  },
  {
    id: "oos-hours-per-truck",
    department: "company",
    division: "company",
    name: "Out-of-Service Hours / Truck",
    formula: "Average downtime per truck",
    owner: "Fleet",
    unit: "hours",
    direction: "lower_better",
    targetLabel: "Down MoM",
    scorecardSection: "company",
    sources: ["fleetio"],
    sourceNote:
      "Fleetio status-change history (when a tractor was set to In Shop / Out of Service and when it left), hours in the month ÷ power units. Trailers are not counted. A once-a-day snapshot is not the clock — the log is.",
    automation: "auto",
    staffKeepAlive:
      "When a tractor is in the shop, set it to In Shop in Fleetio. When it is back on the road, set it to Active. We read that change log for hours. If it sits in the shop but stays Active, this KPI stays 0.",
  },

  // ==== Accounting (9) ======================================================
  {
    id: "billing-cycle-direct",
    department: "accounting",
    division: "company",
    name: "Billing Cycle - Direct Customers",
    formula: "Delivery to invoice (days), direct customers",
    owner: "Billing",
    unit: "days",
    direction: "at_most",
    threshold: 3,
    targetLabel: "≤ 3 days",
    scorecardSection: "accounting",
    sources: ["recorder", "quickmanage"],
    sourceNote:
      "Delivered→invoiced transition time from our status recorder + QM customer type. Caution: recorder has seen 0 'invoiced'/'closed' statuses so far (2026-08-17) - confirm accounting actually marks invoicing in QM (Q13).",
    automation: "semi",
    flags: ["Q11", "Q13: does BB mark trips invoiced in QM?"],
  },
  {
    id: "billing-cycle-factoring",
    department: "accounting",
    division: "company",
    name: "Billing Cycle - Factoring Customers",
    formula: "Delivery to invoice (days), factoring customers",
    owner: "Billing",
    unit: "days",
    direction: "lower_better",
    targetLabel: "TBD (Q3)",
    scorecardSection: null,
    sources: ["recorder", "quickmanage"],
    sourceNote: "Same as direct; how factoring is flagged still needs accounting (Q11). Same Q13 caution: no invoiced statuses recorded yet.",
    automation: "semi",
    flags: ["Q3", "Q11", "Q13: does BB mark trips invoiced in QM?"],
  },
  {
    id: "invoice-accuracy",
    department: "accounting",
    division: "company",
    name: "Invoice Accuracy %",
    formula: "Correct invoices ÷ total invoices",
    owner: "Billing",
    unit: "percent",
    direction: "at_least",
    threshold: 99.5,
    targetLabel: "≥ 99.5%",
    scorecardSection: "accounting",
    sources: ["accounting"],
    sourceNote:
      "Accounting workflow - manual. (Framework's 'invoice error rate' is this metric inverted.) No QuickManage Search or Report has a correct vs incorrect invoice flag.",
    automation: "manual",
  },
  {
    id: "ar-over-30",
    department: "accounting",
    division: "company",
    name: "AR > 30 Days",
    formula: "Accounts receivable older than 30 days",
    owner: "Accounting",
    unit: "usd",
    direction: "lower_better",
    targetLabel: "TBD (Q3)",
    scorecardSection: null,
    sources: ["accounting"],
    sourceNote: "AR aging from the accounting system - manual until Q6.",
    automation: "manual",
    flags: ["Q3", "Q6"],
  },
  {
    id: "ar-over-60",
    department: "accounting",
    division: "company",
    name: "AR > 60 Days",
    formula: "Accounts receivable older than 60 days",
    owner: "Accounting",
    unit: "usd",
    direction: "lower_better",
    targetLabel: "TBD (Q3)",
    scorecardSection: null,
    sources: ["accounting"],
    sourceNote: "AR aging - manual until Q6.",
    automation: "manual",
    flags: ["Q3", "Q6"],
  },
  {
    id: "ar-over-90",
    department: "accounting",
    division: "company",
    name: "AR > 90 Days",
    formula: "Accounts receivable older than 90 days",
    owner: "Accounting",
    unit: "usd",
    direction: "lower_better",
    targetLabel: "TBD (Q3)",
    scorecardSection: null,
    sources: ["accounting"],
    sourceNote: "AR aging - manual until Q6.",
    automation: "manual",
    flags: ["Q3", "Q6"],
  },
  {
    id: "settlement-accuracy",
    department: "accounting",
    division: "company",
    name: "Settlement Accuracy %",
    formula: "Accurate settlements ÷ total (owners/drivers)",
    owner: "Settlements",
    unit: "percent",
    direction: "higher_better",
    targetLabel: "TBD (Q3)",
    scorecardSection: null,
    sources: ["accounting"],
    sourceNote:
      "Settlements/payroll workflow — manual until QuickManage ships the payroll API (being built, 2026-08-26).",
    automation: "manual",
    flags: ["Q3", "Q14"],
  },
  {
    id: "settlement-timeliness",
    department: "accounting",
    division: "company",
    name: "Settlement Timeliness %",
    formula: "On-time settlements ÷ total",
    owner: "Settlements",
    unit: "percent",
    direction: "at_least",
    threshold: 100,
    targetLabel: "100%",
    scorecardSection: "accounting",
    sources: ["accounting"],
    sourceNote:
      "Settlements/payroll workflow — manual until QuickManage ships the payroll API (being built, 2026-08-26).",
    automation: "manual",
    flags: ["Q14"],
  },
  {
    id: "month-end-close-days",
    department: "accounting",
    division: "company",
    name: "Month-End Close Days",
    formula: "Days to close the books",
    owner: "Financial Reporting",
    unit: "days",
    direction: "at_most",
    threshold: 10,
    targetLabel: "≤ 10",
    scorecardSection: "accounting",
    sources: ["accounting"],
    sourceNote:
      "Close checklist date - manual. No QuickManage report is a close calendar. Payroll and Statement Reports will not fill this.",
    automation: "manual",
  },

  // ==== Liquid Tank Dispatch (8) ============================================
  {
    id: "revenue-per-tanker-week",
    department: "tank-dispatch",
    division: "tanker",
    name: "Revenue per Tanker / Week",
    formula: "Tanker completed-trip revenue ÷ tankers that hauled ÷ weeks",
    owner: "Tank Dispatch",
    unit: "usd",
    direction: "info",
    targetLabel: "Set annually",
    scorecardSection: "tanker-ops",
    sources: ["quickmanage", "fleetio"],
    sourceNote:
      "Same rule as company Revenue per Truck / Week, limited to tanker-classified loads: completed-trip dollars ÷ unique truck units that hauled those loads ÷ weeks in the month. Idle tankers are not in the divisor. A load is tanker if the assigned trailer's QuickManage make is a tank OEM (Polar Tank, Brenner, Heil, …), Fleetio type/group says tanker, or that truck only pulls tanker trailers. Unclassified loads stay out of this row (they still count in company-wide Revenue per Truck). Trailer type is blank and is not used — Get Trailer by id is not required for this number.",
    automation: "semi",
    flags: ["Q4-audit"],
  },
  {
    id: "loaded-rate-per-mile-tanker",
    department: "tank-dispatch",
    division: "tanker",
    name: "Loaded Rate per Mile",
    formula: "Total revenue ÷ total miles driven",
    owner: "Tank Dispatch",
    unit: "usdPerMile",
    direction: "higher_better",
    targetLabel: "TBD (Q3)",
    scorecardSection: null,
    sources: ["quickmanage"],
    sourceNote: "QM hauling_rate ÷ stop distance (both fields probe-confirmed).",
    automation: "auto",
    flags: ["Q3", "near-dupe: avg-rate-per-mile"],
  },
  {
    id: "loaded-miles-pct",
    department: "tank-dispatch",
    division: "tanker",
    name: "Loaded Miles %",
    formula: "Loaded miles ÷ total miles",
    owner: "Tank Dispatch",
    unit: "percent",
    direction: "at_least",
    threshold: 80,
    targetLabel: "≥ 80%",
    scorecardSection: "tanker-ops",
    sources: ["quickmanage", "samsara"],
    sourceNote:
      "QM per-stop distance + deadhead fields (probe-confirmed); Samsara odometer deltas as cross-check.",
    automation: "auto",
  },
  {
    id: "empty-miles-pct-tanker",
    department: "tank-dispatch",
    division: "tanker",
    name: "Empty Miles %",
    formula: "Empty miles ÷ total miles",
    owner: "Tank Dispatch",
    unit: "percent",
    direction: "at_most",
    threshold: 20,
    targetLabel: "≤ 20%",
    scorecardSection: "tanker-ops",
    sources: ["quickmanage"],
    sourceNote: "QM deadhead ÷ (distance + deadhead) - fields probe-confirmed.",
    automation: "auto",
  },
  {
    id: "on-time-pickup-tanker",
    department: "tank-dispatch",
    division: "tanker",
    name: "On-Time Pickup %",
    formula: "On-time pickups ÷ total (tanker)",
    owner: "Tank Dispatch",
    unit: "percent",
    direction: "higher_better",
    targetLabel: "TBD (Q3)",
    scorecardSection: null,
    sources: ["quickmanage", "recorder"],
    sourceNote: "QM appointment windows vs actual arrival (Q5).",
    automation: "semi",
    flags: ["Q3", "Q5"],
  },
  {
    id: "on-time-delivery-tanker",
    department: "tank-dispatch",
    division: "tanker",
    name: "On-Time Delivery %",
    formula: "On-time deliveries ÷ total (tanker)",
    owner: "Tank Dispatch",
    unit: "percent",
    direction: "higher_better",
    targetLabel: "TBD (Q3)",
    scorecardSection: null,
    sources: ["quickmanage", "recorder"],
    sourceNote: "QM appointment windows vs actual arrival (Q5).",
    automation: "semi",
    flags: ["Q3", "Q5"],
  },
  {
    id: "customer-service-incidents",
    department: "tank-dispatch",
    division: "tanker",
    name: "Customer Service Incidents",
    formula: "Total incidents per week ÷ total loads",
    owner: "Tank Dispatch",
    unit: "percent",
    direction: "lower_better",
    targetLabel: "TBD (Q3)",
    scorecardSection: null,
    sources: ["manual"],
    sourceNote: "No CRM or LMS — staff log events on this row. Rates still need a typed period value.",
    automation: "manual",
    flags: ["Q3"],
  },
  {
    id: "critical-load-service",
    department: "tank-dispatch",
    division: "tanker",
    name: "Critical Load Service %",
    formula: "Loads delivered within the customer time window",
    owner: "Tank Dispatch",
    unit: "percent",
    direction: "at_least",
    threshold: 99,
    targetLabel: "≥ 99%",
    scorecardSection: "tanker-ops",
    sources: ["quickmanage", "recorder"],
    sourceNote: "QM appointment windows vs actual arrival (Q5).",
    automation: "semi",
    flags: ["Q5"],
  },

  // ==== Dry Van Dispatch (6) ================================================
  {
    id: "revenue-per-van-week",
    department: "dryvan-dispatch",
    division: "dryvan",
    name: "Revenue per Van / Week",
    formula: "Van completed-trip revenue ÷ vans that hauled ÷ weeks",
    owner: "Dry Dispatch",
    unit: "usd",
    direction: "info",
    targetLabel: "Set annually",
    scorecardSection: "dryvan-ops",
    sources: ["quickmanage", "fleetio"],
    sourceNote:
      "Same rule as company Revenue per Truck / Week, limited to dry-van-classified loads: completed-trip dollars ÷ unique truck units that hauled those loads ÷ weeks in the month. Idle vans are not in the divisor. A load is van if the assigned trailer's QuickManage make is a van OEM (Wabash, Great Dane, Utility, …), Fleetio type/group says Dry Van, or that truck only pulls van trailers. January–May 2026 rarely had a trailer on the trip — those months use the truck's usual trailer.",
    automation: "semi",
    flags: ["Q4-audit"],
  },
  {
    id: "loaded-rate-per-mile-van",
    department: "dryvan-dispatch",
    division: "dryvan",
    name: "Loaded Rate per Mile",
    formula: "Total revenue ÷ total miles driven",
    owner: "Dry Dispatch",
    unit: "usdPerMile",
    direction: "higher_better",
    targetLabel: "TBD (Q3)",
    scorecardSection: null,
    sources: ["quickmanage"],
    sourceNote: "Van-classified trips: QM hauling_rate ÷ stop distance. Same tanker-vs-van rule as Revenue per Van / Week.",
    automation: "auto",
    flags: ["Q3"],
  },
  {
    id: "empty-miles-pct-van",
    department: "dryvan-dispatch",
    division: "dryvan",
    name: "Empty Miles %",
    formula: "Empty miles ÷ total miles",
    owner: "Dry Dispatch",
    unit: "percent",
    direction: "at_most",
    threshold: 25,
    targetLabel: "≤ 25%",
    scorecardSection: "dryvan-ops",
    sources: ["quickmanage"],
    sourceNote: "Van-classified trips: QM distance + deadhead. Same tanker-vs-van rule as Revenue per Van / Week.",
    automation: "auto",
  },
  {
    id: "on-time-pickup-van",
    department: "dryvan-dispatch",
    division: "dryvan",
    name: "On-Time Pickup %",
    formula: "On-time pickups ÷ total",
    owner: "Dry Dispatch",
    unit: "percent",
    direction: "at_least",
    threshold: 97,
    targetLabel: "≥ 97%",
    scorecardSection: "dryvan-ops",
    sources: ["quickmanage", "recorder"],
    sourceNote: "QM appointment windows vs actual arrival (Q5).",
    automation: "semi",
    flags: ["Q5"],
  },
  {
    id: "on-time-delivery-van",
    department: "dryvan-dispatch",
    division: "dryvan",
    name: "On-Time Delivery %",
    formula: "On-time deliveries ÷ total",
    owner: "Dry Dispatch",
    unit: "percent",
    direction: "higher_better",
    targetLabel: "TBD (Q3)",
    scorecardSection: null,
    sources: ["quickmanage", "recorder"],
    sourceNote: "QM appointment windows vs actual arrival (Q5).",
    automation: "semi",
    flags: ["Q3", "Q5"],
  },
  {
    id: "driver-utilization-van",
    department: "dryvan-dispatch",
    division: "dryvan",
    name: "Driver Utilization %",
    formula: "Loaded hours ÷ available hours",
    owner: "Dry Dispatch",
    unit: "percent",
    direction: "at_least",
    threshold: 85,
    targetLabel: "≥ 85%",
    scorecardSection: "dryvan-ops",
    sources: ["samsara"],
    sourceNote:
      "Samsara HOS daily logs - but only ~22 of 89 drivers have HOS clocks in this org and logs are near-empty (2026-08-17 probe). BB's ELD likely lives elsewhere; owner to confirm before this can automate.",
    automation: "auto",
    flags: ["Q10: Framework variant uses layover-pay ÷ total-pay", "Q12: which ELD org holds HOS?"],
  },

  // ==== Sales (5) ===========================================================
  {
    id: "new-direct-customers",
    department: "sales",
    division: "tanker",
    name: "New Direct Customers",
    formula: "New active direct accounts acquired",
    owner: "Sales",
    unit: "count",
    direction: "higher_better",
    targetLabel: "Set quarterly",
    scorecardSection: "sales",
    sources: ["manual"],
    sourceNote:
      "No CRM. Log each new direct account on this row; the count fills the period value.",
    automation: "manual",
  },
  {
    id: "avg-rate-per-mile",
    department: "sales",
    division: "tanker",
    name: "Average Rate per Mile",
    formula: "Total revenue ÷ total miles",
    owner: "Sales",
    unit: "usdPerMile",
    direction: "higher_better",
    targetLabel: "TBD (Q3)",
    scorecardSection: null,
    sources: ["quickmanage"],
    sourceNote:
      "We compute this: QM hauling_rate (or stop.rate) ÷ loaded+deadhead miles. Samsara odometer if QM miles are empty.",
    automation: "auto",
    flags: ["Q3", "near-dupe: loaded-rate-per-mile-tanker - Sales to confirm which survives"],
  },
  {
    id: "gross-margin-per-load",
    department: "sales",
    division: "tanker",
    name: "Gross Margin / Load",
    formula: "Revenue − direct cost per load",
    owner: "Sales",
    unit: "usd",
    direction: "higher_better",
    targetLabel: "≥ target",
    scorecardSection: "sales",
    sources: ["accounting", "quickmanage"],
    sourceNote: "Revenue is in QM; cost per load lives in QuickBooks — type it in. No QuickBooks access (Q6).",
    automation: "manual",
    flags: ["Q6"],
  },
  {
    id: "contract-retention",
    department: "sales",
    division: "tanker",
    name: "Contract Retention %",
    formula: "Retained contracts ÷ total",
    owner: "Sales",
    unit: "percent",
    direction: "at_least",
    threshold: 95,
    targetLabel: "≥ 95%",
    scorecardSection: "sales",
    sources: ["manual"],
    sourceNote: "Contract list is tribal knowledge today - manual. (Framework's 'customer retention' merged here.)",
    automation: "manual",
  },
  {
    id: "pipeline-value",
    department: "sales",
    division: "tanker",
    name: "Pipeline Value",
    formula: "Qualified opportunity value",
    owner: "Sales",
    unit: "usd",
    direction: "higher_better",
    targetLabel: "Growing",
    scorecardSection: "sales",
    sources: ["manual"],
    sourceNote: "No CRM. Log each qualified opportunity and its value on this row.",
    automation: "manual",
  },

  // ==== Safety (5) ==========================================================
  {
    id: "dot-recordable-accidents",
    department: "safety",
    division: "company",
    name: "DOT Recordable Accidents",
    formula: "DOT recordable accidents in the period",
    owner: "Safety",
    unit: "count",
    direction: "lower_better",
    targetLabel: "TBD (Q3)",
    scorecardSection: null,
    sources: ["fmcsa", "manual"],
    sourceNote: "FMCSA crash records (public, verified for USDOT 2404871) + the safety log.",
    automation: "semi",
    flags: ["Q3"],
  },
  {
    id: "preventable-accidents",
    department: "safety",
    division: "company",
    name: "Preventable Accident Rate",
    formula: "Preventable accidents per million miles",
    owner: "Safety",
    unit: "perMillionMiles",
    direction: "at_most",
    threshold: 0,
    targetLabel: "0",
    scorecardSection: "tanker-safety",
    sources: ["manual", "samsara"],
    sourceNote:
      "Preventability is the Safety manager's judgment. Log reviewed events here; type the period rate (per million miles) separately. Samsara safety events remain a candidate queue.",
    automation: "manual",
  },
  {
    id: "csa-basic-scores",
    department: "safety",
    division: "company",
    name: "CSA BASIC Scores",
    formula: "FMCSA safety metrics (worst BASIC percentile)",
    owner: "Safety",
    unit: "count",
    direction: "lower_better",
    targetLabel: "Below threshold",
    scorecardSection: "tanker-safety",
    sources: ["fmcsa"],
    sourceNote: "QCMobile API monthly pull for USDOT 2404871 (free webkey - owner signup pending).",
    automation: "auto",
  },
  {
    id: "hazmat-compliance",
    department: "safety",
    division: "tanker",
    name: "HazMat Compliance Rate",
    formula: "Inspections passed ÷ inspections",
    owner: "Safety",
    unit: "percent",
    direction: "at_least",
    threshold: 99,
    targetLabel: "≥ 99%",
    scorecardSection: "tanker-safety",
    sources: ["fmcsa"],
    sourceNote: "FMCSA inspection results; confirm the definition matches management's.",
    automation: "semi",
  },
  {
    id: "training-completion",
    department: "safety",
    division: "company",
    name: "Training Completion %",
    formula: "Required training completed on time",
    owner: "Safety",
    unit: "percent",
    direction: "at_least",
    threshold: 100,
    targetLabel: "100%",
    scorecardSection: "tanker-safety",
    sources: ["manual"],
    sourceNote: "No LMS. Log completed courses here; type the period completion % separately.",
    automation: "manual",
  },

  // ==== Fleet / Maintenance (8) =============================================
  {
    id: "pm-compliance",
    department: "fleet",
    division: "company",
    name: "PM Compliance Rate",
    formula: "Preventive-maintenance services completed on time ÷ due",
    owner: "Fleet",
    unit: "percent",
    direction: "higher_better",
    targetLabel: "TBD (Q3)",
    scorecardSection: null,
    sources: ["fleetio"],
    sourceNote:
      "Fleetio service reminders, point-in-time: % of reminders with a due date that are not overdue.",
    automation: "auto",
    flags: ["Q3"],
  },
  {
    id: "breakdown-frequency-tanker",
    department: "fleet",
    division: "tanker",
    name: "Breakdown Frequency (Tanker)",
    formula: "Breakdowns per truck / month",
    owner: "Fleet",
    unit: "count",
    direction: "lower_better",
    targetLabel: "Down MoM",
    scorecardSection: "fleet-liquid",
    sources: ["fleetio"],
    sourceNote:
      "Fleetio issues by division. Caution: the fleet team has logged no issues since Jan 2026, so 0 means 'nothing recorded', not 'no breakdowns'.",
    automation: "auto",
  },
  {
    id: "breakdown-frequency-van",
    department: "fleet",
    division: "dryvan",
    name: "Breakdown Frequency (Van)",
    formula: "Breakdowns per truck / month",
    owner: "Fleet",
    unit: "count",
    direction: "lower_better",
    targetLabel: "Down MoM",
    scorecardSection: "fleet-dry",
    sources: ["fleetio"],
    sourceNote: "Fleetio issues, dry-van group. Same caution: no issues logged since Jan 2026.",
    automation: "auto",
  },
  {
    id: "avg-downtime-per-event",
    department: "fleet",
    division: "company",
    name: "Avg Downtime per Event",
    formula: "Average downtime per maintenance event",
    owner: "Fleet",
    unit: "hours",
    direction: "lower_better",
    targetLabel: "TBD (Q3)",
    scorecardSection: null,
    sources: ["fleetio"],
    sourceNote:
      "Consecutive down days in our daily Fleetio status snapshots = one event (24h/day), plus historical work orders. Fills as snapshots accumulate (began 2026-08-17).",
    automation: "auto",
    flags: ["Q3"],
  },
  {
    id: "maintenance-cost-per-mile-tanker",
    department: "fleet",
    division: "tanker",
    name: "Maintenance Cost / Mile (Tanker)",
    formula: "Maintenance spend ÷ miles",
    owner: "Fleet",
    unit: "usdPerMile",
    direction: "info",
    targetLabel: "Controlled",
    scorecardSection: "fleet-liquid",
    sources: ["fleetio"],
    sourceNote:
      "Fleetio SERVICE ENTRY totals (the live system - $75k Jul/Aug 2026) + any work orders, ÷ meter deltas or Samsara odometer miles.",
    automation: "auto",
  },
  {
    id: "maintenance-cost-per-mile-van",
    department: "fleet",
    division: "dryvan",
    name: "Maintenance Cost / Mile (Van)",
    formula: "Maintenance spend ÷ miles",
    owner: "Fleet",
    unit: "usdPerMile",
    direction: "info",
    targetLabel: "Controlled",
    scorecardSection: "fleet-dry",
    sources: ["fleetio"],
    sourceNote: "Service-entry + work-order totals ÷ miles, dry-van group.",
    automation: "auto",
  },
  {
    id: "tanker-availability",
    department: "fleet",
    division: "tanker",
    name: "Tanker Availability %",
    formula: "Available tankers ÷ total fleet",
    owner: "Fleet",
    unit: "percent",
    direction: "at_least",
    threshold: 95,
    targetLabel: "≥ 95%",
    scorecardSection: "fleet-liquid",
    sources: ["fleetio"],
    sourceNote:
      "Average of daily status-snapshot availability (began 2026-08-17); current statuses when a window predates snapshots.",
    automation: "auto",
  },
  {
    id: "van-availability",
    department: "fleet",
    division: "dryvan",
    name: "Van Availability %",
    formula: "Available trailers ÷ total",
    owner: "Fleet",
    unit: "percent",
    direction: "at_least",
    threshold: 97,
    targetLabel: "≥ 97%",
    scorecardSection: "fleet-dry",
    sources: ["fleetio"],
    sourceNote: "Average of daily status-snapshot availability, dry-van group.",
    automation: "auto",
  },
];

// ---------------------------------------------------------------------------
// QuickManage vendor waits (2026-08-26) — shown on the KPI portal
// ---------------------------------------------------------------------------

export interface QmWaitingDef {
  id: QmWaitingId;
  /** Default note under the KPI name — same placement as staffKeepAlive. */
  rowNote: string;
  /** Per-KPI override when one API touches rows differently. */
  rowNotes?: Partial<Record<string, string>>;
  title: string;
  vendorStatus: string;
  /** blocked = empty/manual until then; improve = already filling, tighter after; unmapped = no row yet; wont = QM related but cannot fill. */
  effect: "blocked" | "improve" | "unmapped" | "wont";
  detail: string;
  whenShips: string;
}

export const QM_WAITING: QmWaitingDef[] = [
  {
    id: "payroll",
    rowNote:
      "Waiting on a current payroll feed. Statement Reports exist in QuickManage (Earning / Payout / Deductions) but they are saved snapshots, not a live query. The payroll API is still being built. Type this in. This will not fill OR, cash-to-invoice, AR, or close days.",
    title: "Payroll API",
    vendorStatus: "Being built — other companies waiting too (Elbek, 2026-08-26).",
    effect: "blocked",
    detail:
      "No published payroll / driver-payout API. Settlement Accuracy and Settlement Timeliness are the only rows that would auto-fill from it.",
    whenShips:
      "Retest payroll, then we can auto-fill Settlement Accuracy % and Settlement Timeliness %.",
  },
  {
    id: "payroll-partial",
    rowNote: "",
    rowNotes: {
      "gross-margin-per-load":
        "Revenue is already in QuickManage. Cost is QuickBooks (no access). If payroll later has per-load driver pay, that is only part of cost — fuel and other costs would still be missing.",
      "driver-utilization-van":
        "This row uses Samsara HOS hours (blocked until Q12). QuickManage payroll would only matter if we switched to the unused layover-pay formula. Payroll will not fill this row as written.",
    },
    title: "Payroll API (partial)",
    vendorStatus: "Being built (Elbek, 2026-08-26).",
    effect: "improve",
    detail:
      "Payroll is not enough to finish these rows. Gross margin still needs the rest of cost. Driver utilization as written needs HOS, not pay.",
    whenShips:
      "If payroll includes per-load pay we can use it as one cost input on Gross Margin. Driver Utilization stays HOS unless management picks the layover-pay formula.",
  },
  {
    id: "get-trailer",
    rowNote: "",
    title: "Get Trailer by id (type)",
    vendorStatus:
      "Get Driver / Truck / Trailer by id scheduled this week (docs were early; live GET still 404).",
    effect: "improve",
    detail:
      "Not required for tanker/van KPIs. Search trailer type is blank, but trailers Search already has make (Polar Tank → tanker, Wabash → van). Trip dollars and miles come from trips Search. Get Trailer would only help leftover unclassified loads if type is filled there and make is missing — we do not mark those rows as waiting on it.",
    whenShips:
      "Optional retest of GET /x/trailers/{id}. If type is filled we can prefer it over make. Numbers already fill without it.",
  },
  {
    id: "reports",
    rowNote:
      "QuickManage Reports are live with ?type=…&subtype=ignore, but they list saved snapshots staff already ran — not a live query of the current month.",
    rowNotes: {
      "billing-cycle-direct":
        "Trip Report columns include Invoiced Date, Closed Date, and Payment Status. The Reports API only lists saved snapshots, not a live trip query. Trip Search still has no invoiced/closed status (Q13). Not auto-filled yet.",
      "billing-cycle-factoring":
        "Same as direct billing cycle: Trip Report has Invoiced Date / Closed Date on saved snapshots only. Not auto-filled yet (Q13).",
      "ar-over-30":
        "Receivable Report has Age and Balance Dues. GET /x/reports lists saved snapshots, not live aging (a sample week was Sep 2025). Type this in (Q6) until we can pull a current extract.",
      "ar-over-60":
        "Receivable Report has Age and Balance Dues. Saved snapshots only — not a live AR feed. Type this in (Q6) until we can pull a current extract.",
      "ar-over-90":
        "Receivable Report has Age and Balance Dues. Saved snapshots only — not a live AR feed. Type this in (Q6) until we can pull a current extract.",
    },
    title: "Reports (type + subtype=ignore)",
    vendorStatus:
      "Live 2026-08-26 with ?type=trip|statement|receivable|…&subtype=ignore (Elbek: always subtype=ignore). Bare GET /x/reports is still empty.",
    effect: "improve",
    detail:
      "List + /content return spreadsheet snapshots. trip/statement/receivable/1099/adjustment/inspection/employee have saved reports; fuel/toll/maintenance/driver-perf have none. Do not treat snapshots as the current month.",
    whenShips:
      "Wire collectors only if we can generate or fetch a current-period report. Until then type AR/settlements in; billing cycle still waits on invoiced dates.",
  },
  {
    id: "qm-cannot",
    rowNote: "",
    rowNotes: {
      "operating-ratio":
        "QuickManage has the revenue side (trips Search). It has no operating expense. Statement Reports are driver pay, not the P&L. Reports / payroll will not fill this. Type the QuickBooks number in — we do not have QuickBooks access.",
      "cash-to-invoice-days":
        "Receivable Report has Customer Paid Amt (dollars) and Invoiced Date, not a cash/paid date. Trip Report has Payment Status, not days-to-cash. QuickManage will not fill this. Type the QuickBooks number in — we do not have QuickBooks access.",
      "invoice-accuracy":
        "No QuickManage Search or Report has a correct vs incorrect invoice flag. Reports / payroll will not fill this. Type the QuickBooks number in — we do not have QuickBooks access.",
      "month-end-close-days":
        "No QuickManage report is a close calendar. Reports / payroll will not fill this. Type the QuickBooks number in — we do not have QuickBooks access.",
    },
    title: "QuickManage will not fill this",
    vendorStatus:
      "Related to QM: revenue, invoices, and pay show up in Search/Reports, but not the field this KPI needs.",
    effect: "wont",
    detail:
      "OR needs P&L expense. Cash-to-invoice needs a paid date. Invoice accuracy needs a correct/incorrect flag. Close days need a close checklist. None of those are in QM Search, Reports, or payroll.",
    whenShips:
      "Do not wait on Elbek or QuickBooks login for these four. Staff type the number they already see in QuickBooks.",
  },
];

const QM_WAITING_KPI_IDS: Record<QmWaitingId, readonly string[]> = {
  payroll: ["settlement-accuracy", "settlement-timeliness"],
  "payroll-partial": ["gross-margin-per-load", "driver-utilization-van"],
  "get-trailer": [],
  reports: [
    "billing-cycle-direct",
    "billing-cycle-factoring",
    "ar-over-30",
    "ar-over-60",
    "ar-over-90",
  ],
  "qm-cannot": [
    "operating-ratio",
    "cash-to-invoice-days",
    "invoice-accuracy",
    "month-end-close-days",
  ],
};

{
  const byKpi = new Map<string, QmWaitingId[]>();
  for (const item of QM_WAITING) {
    for (const id of QM_WAITING_KPI_IDS[item.id]) {
      const list = byKpi.get(id) ?? [];
      list.push(item.id);
      byKpi.set(id, list);
    }
  }
  for (const def of KPI_DEFS) {
    const waiting = byKpi.get(def.id);
    if (waiting) def.qmWaiting = waiting;
  }
}

// ---------------------------------------------------------------------------
// Not measurable with today's data (audit 2026-08-26 — INC-002)
// ---------------------------------------------------------------------------

/**
 * On-time needs an ACTUAL arrival time. All we have is our recorder's
 * `observed_at`, which is when a dispatcher keyed the status into QuickManage:
 * all 195 delivered transitions in the sample fell between 07:00 and 18:59
 * Chicago, none overnight, 65 on a Monday. Scoring that against the
 * appointment produced 8.3% on-time — a measure of back-office lag, biased low
 * in one direction. The collector no longer publishes these rows
 * (PUBLISH_RECORDER_ONTIME in supabase/functions/kpi-collect/ontime.ts).
 */
const ONTIME_NOT_MEASURABLE =
  "Not measurable yet. QuickManage stores the scheduled appointment but no actual arrival time, and our recorder only sees when dispatch keyed the status in (office hours only), so every load scores late. Needs Samsara geofence arrival or a QuickManage arrival field before this shows a percentage.";

export const NOT_MEASURABLE_KPI_IDS: Readonly<Record<string, string>> = {
  "on-time-pickup": ONTIME_NOT_MEASURABLE,
  "on-time-delivery": ONTIME_NOT_MEASURABLE,
  "on-time-pickup-tanker": ONTIME_NOT_MEASURABLE,
  "on-time-delivery-tanker": ONTIME_NOT_MEASURABLE,
  "on-time-pickup-van": ONTIME_NOT_MEASURABLE,
  "on-time-delivery-van": ONTIME_NOT_MEASURABLE,
  "critical-load-service":
    `${ONTIME_NOT_MEASURABLE} "Critical" is also still undefined, so this row was a copy of tanker on-time delivery.`,
};

{
  for (const def of KPI_DEFS) {
    const reason = NOT_MEASURABLE_KPI_IDS[def.id];
    if (reason) def.notMeasurable = reason;
  }
}

export function qmWaitingFor(def: KpiDef): QmWaitingDef[] {
  return (def.qmWaiting ?? [])
    .map((id) => {
      const item = QM_WAITING.find((entry) => entry.id === id);
      if (!item) return null;
      const rowNote = item.rowNotes?.[def.id] ?? item.rowNote;
      if (!rowNote) return null;
      return { ...item, rowNote };
    })
    .filter((item): item is QmWaitingDef => Boolean(item));
}

export function kpisWaitingOn(id: QmWaitingId): KpiDef[] {
  return KPI_DEFS.filter((def) => def.qmWaiting?.includes(id));
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format a stored numeric value for display in the KPI's unit. */
export function formatKpiValue(value: number, unit: KpiUnit): string {
  switch (unit) {
    case "percent":
      return `${value}%`;
    case "days":
      return `${value} d`;
    case "usd":
      return `$${value.toLocaleString("en-US")}`;
    case "usdPerMile":
      return `$${value.toFixed(2)}/mi`;
    case "hours":
      return `${value} hrs`;
    case "perMillionMiles":
      return `${value}/MM mi`;
    default:
      return String(value);
  }
}

/**
 * On/off target vs the numeric threshold; null when the KPI has no threshold.
 *
 * A `notMeasurable` KPI is always null: you cannot be on or off a target you
 * cannot measure. This is the choke point that keeps a stale row out of red
 * counts, the Scorecard's red-only filter, its action-item list, and CSV
 * status — see INC-002, where 8.3% on-time would have shown up in all four.
 */
export function kpiOnTarget(def: KpiDef, value: number): boolean | null {
  if (def.notMeasurable) return null;
  if (def.direction === "at_most" && def.threshold !== undefined) return value <= def.threshold;
  if (def.direction === "at_least" && def.threshold !== undefined) return value >= def.threshold;
  return null;
}

/** Is a change from `prev` to `value` an improvement for this KPI? Null when flat/unknown. */
export function kpiImproved(def: KpiDef, value: number, prev: number): boolean | null {
  if (def.notMeasurable) return null;
  if (value === prev) return null;
  const up = value > prev;
  switch (def.direction) {
    case "at_least":
    case "higher_better":
      return up;
    case "at_most":
    case "lower_better":
      return !up;
    default:
      return null;
  }
}

/**
 * Working sources first (Auto, then Semi-auto), No source / manual last.
 * Order inside each group stays the Framework catalog order.
 */
export function sortKpisForDisplay(defs: KpiDef[]): KpiDef[] {
  const rank: Record<KpiAutomation, number> = { auto: 0, semi: 1, manual: 2 };
  return [...defs].sort((a, b) => rank[a.automation] - rank[b.automation]);
}

/** All KPIs of one Framework department, working sources first. */
export function kpisForDepartment(department: Department): KpiDef[] {
  return sortKpisForDisplay(KPI_DEFS.filter((d) => d.department === department));
}

/** All KPIs of one Scorecard section (the executive view). */
export function kpisForScorecardSection(section: ScorecardSection): KpiDef[] {
  return sortKpisForDisplay(KPI_DEFS.filter((d) => d.scorecardSection === section));
}

export const AUTOMATION_LABEL: Record<KpiAutomation, string> = {
  auto: "Automatic",
  semi: "Semi-automatic",
  manual: "Manual entry",
};

export const SOURCE_LABEL: Record<KpiSourceSystem, string> = {
  quickmanage: "QuickManage",
  samsara: "Samsara",
  fleetio: "Fleetio",
  fmcsa: "FMCSA",
  recorder: "Status recorder",
  accounting: "Accounting",
  manual: "Manual",
};

/** Why an empty row is empty — for the department-by-department test pass. */
export function kpiWaitHint(def: KpiDef): string {
  if (def.notMeasurable) {
    return def.notMeasurable;
  }
  if (def.qmWaiting?.includes("payroll")) {
    return "Waiting on QuickManage payroll API (still being built). Type this in until then.";
  }
  if (def.qmWaiting?.includes("qm-cannot")) {
    return "Type this in from QuickBooks. We do not have QuickBooks access. QuickManage Search, Reports, and payroll will not fill this.";
  }
  if (def.sources.includes("fmcsa")) {
    return "Waiting on the free FMCSA webkey.";
  }
  if (def.sources.includes("recorder")) {
    return "Needs a real load status change after the recorder started. First snapshots are not arrival times.";
  }
  if (def.sources.includes("accounting") && def.automation !== "auto") {
    return "Type this in from QuickBooks (no access). QuickManage payroll will not fill this — that is driver settlements, not the P&L / AR.";
  }
  if (LOG_KPI_IDS.has(def.id)) {
    return "Expand the row to log events, or type the period value.";
  }
  if (def.automation === "manual") {
    return "Type this in — no live system records it yet.";
  }
  if (def.automation === "auto") {
    return "Click Load automatic values, or Refresh from systems if the last pull missed this row.";
  }
  return "Partial automation — expand the row for the source note.";
}
